import React, { useState } from 'react';
import { Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import { GlobalAnalysisFilters } from '../../types';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

interface GlobalFilterPanelProps {
  filters: GlobalAnalysisFilters;
  onFiltersChange: (filters: GlobalAnalysisFilters) => void;
  totalResults: number;
}

export const GlobalFilterPanel: React.FC<GlobalFilterPanelProps> = ({
  filters,
  onFiltersChange,
  totalResults
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleReset = () => {
    onFiltersChange({
      dateRange: [null, null],
      marketCapRange: [0, Infinity],
      gapPercentRange: [0, Infinity],
      priceRange: [0, Infinity],
      volumeThreshold: 0,
      closeType: 'all',
      pmhBreak: 'all'
    });
  };

  const hasActiveFilters =
    filters.dateRange[0] !== null ||
    filters.dateRange[1] !== null ||
    filters.marketCapRange[0] !== 0 ||
    filters.marketCapRange[1] !== Infinity ||
    filters.gapPercentRange[0] !== 0 ||
    filters.gapPercentRange[1] !== Infinity ||
    filters.priceRange[0] !== 0 ||
    filters.priceRange[1] !== Infinity ||
    filters.volumeThreshold !== 0 ||
    filters.closeType !== 'all' ||
    filters.pmhBreak !== 'all';

  return (
    <div className="premium-chart-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="premium-icon-bg">
            <Filter className="h-5 w-5 text-blue-400" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-white">Filtri Globali</h3>
            <p className="text-xs text-gray-400 mt-1">{totalResults} risultati trovati</p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {hasActiveFilters && (
            <button
              onClick={handleReset}
              className="px-3 py-2 text-xs font-medium text-red-400 bg-red-500/10 hover:bg-red-500/20 rounded-lg transition-colors flex items-center space-x-1"
            >
              <X className="h-3 w-3" />
              <span>Reset</span>
            </button>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="px-3 py-2 text-xs font-medium text-blue-400 bg-blue-500/10 hover:bg-blue-500/20 rounded-lg transition-colors flex items-center space-x-1"
          >
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            <span>{isExpanded ? 'Nascondi' : 'Mostra'} Filtri</span>
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="space-y-4 pt-4 border-t border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-2">
                Data Inizio
              </label>
              <input
                type="date"
                value={filters.dateRange[0] ? format(filters.dateRange[0], 'yyyy-MM-dd') : ''}
                onChange={(e) => {
                  const newDate = e.target.value ? new Date(e.target.value) : null;
                  onFiltersChange({ ...filters, dateRange: [newDate, filters.dateRange[1]] });
                }}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-300 mb-2">
                Data Fine
              </label>
              <input
                type="date"
                value={filters.dateRange[1] ? format(filters.dateRange[1], 'yyyy-MM-dd') : ''}
                onChange={(e) => {
                  const newDate = e.target.value ? new Date(e.target.value) : null;
                  onFiltersChange({ ...filters, dateRange: [filters.dateRange[0], newDate] });
                }}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-300 mb-2">
                Gap % Minimo
              </label>
              <input
                type="number"
                value={filters.gapPercentRange[0]}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;
                  onFiltersChange({ ...filters, gapPercentRange: [value, filters.gapPercentRange[1]] });
                }}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-300 mb-2">
                Gap % Massimo
              </label>
              <input
                type="number"
                value={filters.gapPercentRange[1] === Infinity ? '' : filters.gapPercentRange[1]}
                onChange={(e) => {
                  const value = e.target.value === '' ? Infinity : parseFloat(e.target.value);
                  onFiltersChange({ ...filters, gapPercentRange: [filters.gapPercentRange[0], value] });
                }}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Illimitato"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-300 mb-2">
                Prezzo Minimo ($)
              </label>
              <input
                type="number"
                step="0.1"
                value={filters.priceRange[0]}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;
                  onFiltersChange({ ...filters, priceRange: [value, filters.priceRange[1]] });
                }}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-300 mb-2">
                Prezzo Massimo ($)
              </label>
              <input
                type="number"
                step="0.1"
                value={filters.priceRange[1] === Infinity ? '' : filters.priceRange[1]}
                onChange={(e) => {
                  const value = e.target.value === '' ? Infinity : parseFloat(e.target.value);
                  onFiltersChange({ ...filters, priceRange: [filters.priceRange[0], value] });
                }}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Illimitato"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-300 mb-2">
                Market Cap Minimo ($)
              </label>
              <input
                type="number"
                value={filters.marketCapRange[0]}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;
                  onFiltersChange({ ...filters, marketCapRange: [value, filters.marketCapRange[1]] });
                }}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-300 mb-2">
                Market Cap Massimo ($)
              </label>
              <input
                type="number"
                value={filters.marketCapRange[1] === Infinity ? '' : filters.marketCapRange[1]}
                onChange={(e) => {
                  const value = e.target.value === '' ? Infinity : parseFloat(e.target.value);
                  onFiltersChange({ ...filters, marketCapRange: [filters.marketCapRange[0], value] });
                }}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Illimitato"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-300 mb-2">
                Volume PM Minimo
              </label>
              <input
                type="number"
                value={filters.volumeThreshold}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;
                  onFiltersChange({ ...filters, volumeThreshold: value });
                }}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-300 mb-2">
                Tipo Chiusura
              </label>
              <select
                value={filters.closeType}
                onChange={(e) => {
                  onFiltersChange({ ...filters, closeType: e.target.value as 'all' | 'red' | 'green' });
                }}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Tutte</option>
                <option value="red">Solo Rosse</option>
                <option value="green">Solo Verdi</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-300 mb-2">
                PMH Break
              </label>
              <select
                value={filters.pmhBreak}
                onChange={(e) => {
                  onFiltersChange({ ...filters, pmhBreak: e.target.value as 'all' | 'break' | 'no-break' });
                }}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Tutti</option>
                <option value="break">Solo Break</option>
                <option value="no-break">Solo No Break</option>
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
